import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api-client';

/** A tenant's own role. `baseRole` is the level role-gated screens see. */
export interface TenantRole {
  id: string;
  name: string;
  baseRole: string;
  permissions: string[];
  userCount: number;
}

export type TenantRoleInput = Pick<TenantRole, 'name' | 'baseRole' | 'permissions'>;

export function useTenantRoles(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['tenant-roles'],
    queryFn: () => authApi.get<{ data: TenantRole[] }>('/tenant-roles'),
    enabled: options.enabled ?? true,
  });
}

function useRefreshRoles() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['tenant-roles'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };
}

export function useCreateTenantRole() {
  const onSuccess = useRefreshRoles();
  return useMutation({
    mutationFn: (data: TenantRoleInput) => authApi.post<{ data: TenantRole }>('/tenant-roles', data),
    onSuccess,
  });
}

export function useUpdateTenantRole() {
  const onSuccess = useRefreshRoles();
  return useMutation({
    mutationFn: ({ id, ...data }: TenantRoleInput & { id: string }) =>
      authApi.put<{ data: TenantRole }>(`/tenant-roles/${id}`, data),
    onSuccess,
  });
}

export function useDeleteTenantRole() {
  const onSuccess = useRefreshRoles();
  return useMutation({
    mutationFn: (id: string) => authApi.delete(`/tenant-roles/${id}`),
    onSuccess,
  });
}
